import { isFilterableReason, isProvider } from './reasons';

import type { FilterableRefusalReason, RefusalProvider } from './reasons';

/**
 * The viewer's filters, as the URL holds them (design 04 §4.3).
 *
 * The URL is the only source of the query, so a copied link reproduces the
 * view and an alert's link opens it already filtered. Everything here is a
 * typed value: there is no free-text search, and there is no tenant, on
 * purpose (every record is one no tenant could be named for).
 *
 * `from`/`to` are both present or both absent. Absent is the rolling last 24
 * hours, resolved against a `now` the page captures once, so a copied link
 * without a window keeps meaning "the last day" instead of a fixed past day.
 */
export interface ViewerFilters {
    readonly from?: string;
    readonly to?: string;
    /** The alert that opened the screen. Context only; never sent to the API. */
    readonly type?: AlertType;
    readonly domain?: string;
    readonly reasons: readonly FilterableRefusalReason[];
    readonly provider?: RefusalProvider;
}

export const ALERT_TYPES = ['refusals-domain', 'refusals-platform'] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

/** What a list, summary or export request carries. The same object for all three. */
export interface RequestFilters {
    readonly from: string;
    readonly to: string;
    readonly reasons: readonly FilterableRefusalReason[];
    readonly provider?: RefusalProvider;
    readonly domain?: string;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export const DEFAULT_WINDOW_HOURS = 24;
/** The log is kept twelve months; the BFF refuses a wider range. */
export const MAX_RANGE_DAYS = 366;

/** The same pattern the BFF validates against. */
const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/;

function isAlertType(value: string): value is AlertType {
    return (ALERT_TYPES as readonly string[]).includes(value);
}

function isoOrNull(value: string | null): string | null {
    if (value === null || value.trim() === '') return null;
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : new Date(time).toISOString();
}

/** A malformed window is ignored, never an error: the page falls back to the last 24 hours. */
function parseWindow(search: URLSearchParams): { from?: string; to?: string } {
    const from = isoOrNull(search.get('from'));
    const to = isoOrNull(search.get('to'));
    if (from === null || to === null) return {};
    const span = Date.parse(to) - Date.parse(from);
    if (span <= 0 || span > MAX_RANGE_DAYS * DAY_MS) return {};
    return { from, to };
}

export function parseFilters(search: URLSearchParams): ViewerFilters {
    const type = search.get('type') ?? '';
    const domain = (search.get('domain') ?? '').trim().toLowerCase();
    const provider = search.get('provider') ?? '';
    const reasons = [...new Set(search.getAll('reason').filter(isFilterableReason))];

    return {
        ...parseWindow(search),
        ...(isAlertType(type) ? { type } : {}),
        ...(DOMAIN_PATTERN.test(domain) ? { domain } : {}),
        reasons,
        ...(isProvider(provider) ? { provider } : {}),
    };
}

/** Writes only what {@link parseFilters} reads, so nothing else ever reaches a link. */
export function serializeFilters(filters: ViewerFilters): URLSearchParams {
    const search = new URLSearchParams();
    if (filters.from !== undefined && filters.to !== undefined) {
        search.set('from', filters.from);
        search.set('to', filters.to);
    }
    if (filters.type !== undefined) search.set('type', filters.type);
    if (filters.domain !== undefined) search.set('domain', filters.domain);
    for (const reason of filters.reasons) search.append('reason', reason);
    if (filters.provider !== undefined) search.set('provider', filters.provider);
    return search;
}

/**
 * Whether the window moves with `now` (no `from`/`to`: the rolling last 24
 * hours) or is fixed (both set — an alert link, or a custom range). A caller
 * that wants fresh data for a FIXED window cannot get it by changing `now`,
 * because {@link toRequestFilters} ignores `now` in that case: it must
 * `refetch()` instead.
 */
export function isRollingWindow(filters: Pick<ViewerFilters, 'from' | 'to'>): boolean {
    return filters.from === undefined || filters.to === undefined;
}

/** The filters a request sends: the window resolved, the alert type left out. */
export function toRequestFilters(filters: ViewerFilters, now: Date): RequestFilters {
    // Not `isRollingWindow(filters)`: TypeScript can narrow `filters.from`/`to`
    // to `string` below from this exact check, but not from a boolean handed
    // back through a function call.
    const rolling = filters.from === undefined || filters.to === undefined;
    return {
        from: rolling
            ? new Date(now.getTime() - DEFAULT_WINDOW_HOURS * HOUR_MS).toISOString()
            : filters.from,
        to: rolling ? now.toISOString() : filters.to,
        reasons: filters.reasons,
        ...(filters.provider !== undefined ? { provider: filters.provider } : {}),
        ...(filters.domain !== undefined ? { domain: filters.domain } : {}),
    };
}

export const RANGE_PRESETS = ['24h', '7d', '30d'] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

const PRESET_DAYS: Record<Exclude<RangePreset, '24h'>, number> = { '7d': 7, '30d': 30 };

/** 24 h is the rolling default and writes no window; the others end now. */
export function presetWindow(preset: RangePreset, now: Date): { from?: string; to?: string } {
    if (preset === '24h') return {};
    return {
        from: new Date(now.getTime() - PRESET_DAYS[preset] * DAY_MS).toISOString(),
        to: now.toISOString(),
    };
}
